import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  FaDatabase,
  FaChartLine,
  FaFileAlt,
  FaShieldAlt,
  FaArrowRight,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.5, ease: "easeOut" },
  }),
};

const features = [
  {
    icon: <FaDatabase />,
    color: "#6366f1",
    title: "Structured Data Management",
    description:
      "Flexible data grids with validation, multi-tenant isolation, and real-time updates.",
  },
  {
    icon: <FaChartLine />,
    color: "#8b5cf6",
    title: "Inventory & Analytics",
    description:
      "Live inventory monitoring with predictive and statistical analysis tools.",
  },
  {
    icon: <FaFileAlt />,
    color: "#0ea5e9",
    title: "Reports & Insights",
    description:
      "Analyst-generated reports with regression, classification, and forecasting.",
  },
  {
    icon: <FaShieldAlt />,
    color: "#10b981",
    title: "Secure & Multi-Tenant",
    description:
      "Role-based access, organization isolation, and full audit trails built in.",
  },
];

function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  React.useEffect(() => {
    if (user) {
      if (user.role === "CLIENT") navigate("/client");
      else if (user.role === "ANALYST") navigate("/analyst");
      else if (user.role === "ADMIN") navigate("/admin");
    }
  }, [user, navigate]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #fafbff 0%, #ffffff 40%, #f8fafc 100%)",
        color: "#0f172a",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle background accents */}
      <div
        style={{
          position: "absolute",
          top: "-120px",
          right: "-80px",
          width: "400px",
          height: "400px",
          background:
            "radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-100px",
          left: "-60px",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 24px", position: "relative" }}>
        {/* Header */}
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "24px 0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 700,
                fontSize: "14px",
                letterSpacing: "-0.5px",
              }}
            >
              D
            </div>
            <span style={{ fontSize: "18px", fontWeight: 600, letterSpacing: "-0.3px" }}>
              DSTL
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <LanguageSwitcher />
            <Link
              to="/login"
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#475569",
                textDecoration: "none",
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = "#6366f1";
                e.target.style.color = "#6366f1";
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = "#e2e8f0";
                e.target.style.color = "#475569";
              }}
            >
              {t("auth.signIn", "Sign In")}
            </Link>
          </div>
        </header>

        {/* Hero */}
        <section style={{ paddingTop: "80px", paddingBottom: "100px" }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            style={{
              display: "inline-block",
              fontSize: "13px",
              fontWeight: 500,
              color: "#6366f1",
              background: "rgba(99,102,241,0.08)",
              padding: "6px 14px",
              borderRadius: "20px",
              marginBottom: "24px",
              letterSpacing: "0.3px",
            }}
          >
            Multi-Tenant Data Platform
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            style={{
              fontSize: "clamp(36px, 5vw, 60px)",
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-1.5px",
              maxWidth: "700px",
              margin: 0,
            }}
          >
            Enterprise Data
            <br />
            Intelligence{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Platform
            </span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            style={{
              marginTop: "24px",
              fontSize: "18px",
              lineHeight: 1.7,
              color: "#64748b",
              maxWidth: "540px",
            }}
          >
            Manage operational data, monitor inventory in real-time, and
            generate intelligent insights — all in one secure platform.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={3}
            style={{ marginTop: "40px", display: "flex", gap: "12px", alignItems: "center" }}
          >
            <Link
              to="/login"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "14px 28px",
                background: "linear-gradient(135deg, #6366f1, #7c3aed)",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 600,
                borderRadius: "10px",
                textDecoration: "none",
                boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 6px 20px rgba(99,102,241,0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 14px rgba(99,102,241,0.35)";
              }}
            >
              {t("home.getStarted", "Get Started")}
              <FaArrowRight size={13} />
            </Link>

            <a
              href="#features"
              style={{
                padding: "14px 24px",
                fontSize: "15px",
                fontWeight: 500,
                color: "#475569",
                textDecoration: "none",
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.color = "#6366f1")}
              onMouseLeave={(e) => (e.target.style.color = "#475569")}
            >
              {t("home.learnMore", "Learn More")} ↓
            </a>
          </motion.div>
        </section>

        {/* Features */}
        <section id="features" style={{ paddingBottom: "100px" }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "24px",
            }}
          >
            {features.map((f, i) => (
              <FeatureCard key={i} {...f} index={i} />
            ))}
          </motion.div>
        </section>

        {/* Stats / Trust bar */}
        <section
          style={{
            paddingBottom: "80px",
            display: "flex",
            justifyContent: "center",
            gap: "48px",
            flexWrap: "wrap",
          }}
        >
          {[
            { value: "99.9%", label: "Uptime" },
            { value: "Multi-Tenant", label: "Architecture" },
            { value: "Real-Time", label: "Analytics" },
            { value: "End-to-End", label: "Encryption" },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              style={{ textAlign: "center" }}
            >
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: 700,
                  color: "#0f172a",
                  letterSpacing: "-0.3px",
                }}
              >
                {stat.value}
              </div>
              <div style={{ fontSize: "13px", color: "#94a3b8", marginTop: "4px" }}>
                {stat.label}
              </div>
            </motion.div>
          ))}
        </section>

        {/* Footer */}
        <footer
          style={{
            borderTop: "1px solid #f1f5f9",
            padding: "24px 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "13px",
            color: "#94a3b8",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <span>© {new Date().getFullYear()} Data Space Technologies Ltd</span>
          <div style={{ display: "flex", gap: "24px" }}>
            {["Privacy", "Terms", "Contact"].map((item) => (
              <Link
                key={item}
                to={`/${item.toLowerCase()}`}
                style={{
                  color: "#94a3b8",
                  textDecoration: "none",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.color = "#6366f1")}
                onMouseLeave={(e) => (e.target.style.color = "#94a3b8")}
              >
                {item}
              </Link>
            ))}
          </div>
        </footer>
      </div>
    </div>
  );
}

function FeatureCard({ icon, color, title, description, index }) {
  return (
    <motion.div
      variants={fadeUp}
      custom={index}
      whileHover={{ y: -4 }}
      style={{
        background: "#ffffff",
        border: "1px solid #f1f5f9",
        borderRadius: "16px",
        padding: "28px 24px",
        cursor: "default",
        transition: "box-shadow 0.25s ease, border-color 0.25s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.06)";
        e.currentTarget.style.borderColor = "#e2e8f0";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = "#f1f5f9";
      }}
    >
      <div
        style={{
          width: "44px",
          height: "44px",
          borderRadius: "12px",
          background: `${color}12`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: color,
          fontSize: "20px",
          marginBottom: "20px",
        }}
      >
        {icon}
      </div>
      <h3
        style={{
          fontSize: "16px",
          fontWeight: 600,
          marginBottom: "8px",
          letterSpacing: "-0.2px",
          margin: "0 0 8px 0",
        }}
      >
        {title}
      </h3>
      <p
        style={{
          fontSize: "14px",
          lineHeight: 1.6,
          color: "#64748b",
          margin: 0,
        }}
      >
        {description}
      </p>
    </motion.div>
  );
}

export default Home;